import { FC } from "react";
import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { DeleteOutlineOutlined, Visibility } from "@mui/icons-material";
import { HexTile } from "../../../../../../components/ui";
import { ProductModel } from "../../../../../../models/Product.model";
import ConcentrationInput from "./ConcentrationInput";

type Props = {
    product: ProductModel;
    onConcentrationChange: (id: number, value: string) => void;
    onInfo: (product: ProductModel) => void;
    onDelete: (product: ProductModel) => void;
};

/** Linha de um ativo na etapa de doses (hex + nome + concentração + ações). */
const ActiveDoseRow: FC<Props> = ({ product, onConcentrationChange, onInfo, onDelete }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: "12px 16px",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "13px",
                backgroundColor: "background.paper",
                "@media (max-width: 360px)": {
                    display: "grid",
                    gridTemplateAreas: `"hex name actions" "hex concentration actions"`,
                    gridTemplateColumns: "34px minmax(0, 1fr) 32px",
                    columnGap: 1,
                    rowGap: 1,
                },
            }}
        >
            {/* HexTile */}
            <Box sx={{ gridArea: "hex" }}>
                <HexTile label={product.productName} size={34} />
            </Box>

            {/* Name */}
            <Box sx={{ gridArea: "name", flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "text.primary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        "@media (max-width: 360px)": {
                            whiteSpace: "normal",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflowWrap: "anywhere",
                        },
                    }}
                >
                    {product.productName}
                </Typography>
                {product.showConcentration && (
                    <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                        {product.minimumDosage}% – {product.maxDosage}%
                    </Typography>
                )}
            </Box>

            {/* Concentration input — same component/validation as before */}
            {product.showConcentration && (
                <Box sx={{ gridArea: "concentration", justifySelf: "start" }}>
                    <ConcentrationInput product={product} onChange={onConcentrationChange} />
                </Box>
            )}

            {/* Action buttons */}
            <Box sx={{ gridArea: "actions", display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Tooltip title="Informações" placement="top">
                    <IconButton size="small" onClick={() => onInfo(product)} sx={{ color: "primary.main" }}>
                        <Visibility sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Remover o ativo" placement="top">
                    <IconButton size="small" onClick={() => onDelete(product)} sx={{ color: "text.secondary" }}>
                        <DeleteOutlineOutlined sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};

export default ActiveDoseRow;
